/**
 * Small hand-rolled SVG charts — a donut, a horizontal bar list and a
 * mini line chart. Deliberately dependency-free (no Recharts): the dashboard
 * needs three simple shapes, and inline SVG keeps them theme-aware via the
 * same CSS variables as everything else.
 */

const PALETTE = [
  "hsl(217 91% 60%)",
  "hsl(160 84% 39%)",
  "hsl(38 92% 50%)",
  "hsl(0 84% 60%)",
  "hsl(280 65% 60%)",
  "hsl(190 90% 40%)",
  "hsl(240 5% 65%)",
];

export function DonutChart({
  data,
}: {
  data: { label: string; value: number }[];
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const size = 160;
  const stroke = 26;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;

  if (total === 0) {
    return <p className="text-sm text-muted-foreground">No data for this period.</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-5">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Breakdown" className="shrink-0">
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {data.map((d, i) => {
            const frac = d.value / total;
            const dash = frac * c;
            const seg = (
              <circle
                key={d.label}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={PALETTE[i % PALETTE.length]}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${c - dash}`}
                strokeDashoffset={-offset}
              />
            );
            offset += dash;
            return seg;
          })}
        </g>
        <text
          x="50%"
          y="50%"
          textAnchor="middle"
          dominantBaseline="central"
          className="fill-foreground text-lg font-semibold"
        >
          {total}
        </text>
      </svg>
      <ul className="space-y-1 text-sm">
        {data.map((d, i) => (
          <li key={d.label} className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: PALETTE[i % PALETTE.length] }}
            />
            <span className="capitalize text-muted-foreground">{d.label.replace(/_/g, " ")}</span>
            <span className="ml-auto font-medium tabular-nums">{d.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function BarList({
  data,
}: {
  data: { label: string; value: number }[];
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  if (data.every((d) => d.value === 0)) {
    return <p className="text-sm text-muted-foreground">No data for this period.</p>;
  }
  return (
    <ul className="space-y-2">
      {data.map((d, i) => (
        <li key={d.label} className="text-sm">
          <div className="mb-0.5 flex justify-between">
            <span className="capitalize text-muted-foreground">{d.label.replace(/_/g, " ")}</span>
            <span className="font-medium tabular-nums">{d.value}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full"
              style={{ width: `${(d.value / max) * 100}%`, background: PALETTE[i % PALETTE.length] }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

export function MiniLine({
  data,
}: {
  data: { date: string; count: number; resolved?: number }[];
}) {
  const w = 520;
  const h = 120;
  const pad = 8;
  if (data.length === 0) {
    return <p className="text-sm text-muted-foreground">No tickets created in this period.</p>;
  }
  const hasResolved = data.some((d) => d.resolved != null);
  const max = Math.max(
    1,
    ...data.map((d) => Math.max(d.count, d.resolved ?? 0)),
  );
  const step = data.length > 1 ? (w - 2 * pad) / (data.length - 1) : 0;

  const seriesPoints = (key: "count" | "resolved") =>
    data
      .map((d, i) => {
        const x = pad + i * step;
        const y = h - pad - ((d[key] ?? 0) / max) * (h - 2 * pad);
        return `${x},${y}`;
      })
      .join(" ");

  const created = seriesPoints("count");
  const area = `${pad},${h - pad} ${created} ${pad + (data.length - 1) * step},${h - pad}`;

  return (
    <div>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        role="img"
        aria-label="Tickets created and resolved per day"
        className="h-28 w-full"
      >
        <polygon points={area} fill="hsl(217 91% 60% / 0.12)" />
        <polyline
          points={created}
          fill="none"
          stroke="hsl(217 91% 60%)"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
        />
        {hasResolved && (
          <polyline
            points={seriesPoints("resolved")}
            fill="none"
            stroke="hsl(142 71% 45%)"
            strokeWidth={2}
            vectorEffect="non-scaling-stroke"
          />
        )}
      </svg>
      {hasResolved && (
        <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-sm" style={{ background: "hsl(217 91% 60%)" }} />
            Created
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-3 rounded-sm" style={{ background: "hsl(142 71% 45%)" }} />
            Resolved
          </span>
        </div>
      )}
    </div>
  );
}
